import type { SupportedExtensions } from 'constants/export';
import type { AppId } from 'constants/known-apps';
import { SHOW_HOUR_EDITOR_ATTACHMENTS, USER_PROPERTIES_EVENT_NAMES } from 'constants/user';
import type { ExportReportTrackingSource } from 'redux/modules/superreports.types';
import type {
  AllAcceptedUserPropertyKey,
  CustomEventNameUserPropertyKey,
  UserPropertyValue,
} from 'redux/modules/user.types';
import { isUserPropertyInBlacklist } from 'redux/modules/user.types';
import type { Filter } from 'redux/modules/users-page/users-dashboard/usersDashboard.types';
import { getFilter as getCalendarFilter } from 'redux/selectors/calendar.filter';
import { getHourDraft } from 'redux/selectors/hours.draft.base';
import { getMemoryViewmode } from 'redux/selectors/timeline';
import { getUserPropertyAsBoolean } from 'redux/selectors/user';
import type { IState } from 'redux/state';
import TimelyAnalytics from 'tracking/timely-analytics';
import type { DownloadMemorySource, ViewedBannerOptions, ViewedHintOptions } from './constants';
import {
  ADDED_MEMORY_TO_ENTRY,
  CONNECTED_APP,
  DOWNLOAD_MEMORY_TRACKER,
  EXPORTED_REPORT,
  INITIATED_USER_ACTION,
  SIGNED_UP,
  SIGNED_UP_DATALAYER,
  SNOOZE_MEMORY_BANNER,
  TRAINED_MEMORY,
  TRIAL_STARTED,
  TRIAL_STARTED_DATALAYER,
  UPGRADE_OPPORTUNITY,
  USER_DASHBOARD_FILTER_CHANGED,
  VIEWED_BANNER,
  VIEWED_HINT,
  VIEWED_SUGGESTION_DROPDOWN,
  VIEWED_TERMS,
} from './constants';
import type { TrackingProperties } from './types';

const viewedHint = (id: ViewedHintOptions, fullText: string) => {
  const params = { id, fullText };
  TimelyAnalytics.track(VIEWED_HINT, params);
};
const viewedBanner = (id: ViewedBannerOptions, fullText: string) => {
  const params = { id, fullText };
  TimelyAnalytics.track(VIEWED_BANNER, params);
};

const viewedSuggestionDropdown = () => {
  TimelyAnalytics.track(
    VIEWED_SUGGESTION_DROPDOWN,
    { modal_name: 'Memory AI' },
    { integrations: { Intercom: false } }
  );
};

const trainedMemory = () => {
  TimelyAnalytics.track(TRAINED_MEMORY, {}, { integrations: { Intercom: false } });
};

const viewedTerms = () => {
  TimelyAnalytics.track(VIEWED_TERMS, {});
};

const userDashboardFilterChanged = (
  filter: Partial<Pick<Filter, 'mode' | 'period' | 'percent'>>
) => {
  const filterProps = {
    ...(filter.percent !== undefined && { percent: filter.percent }),
    ...(filter.period !== undefined && { period: filter.period }),
    ...(filter.mode !== undefined && { mode: filter.mode }),
  };

  TimelyAnalytics.track(
    USER_DASHBOARD_FILTER_CHANGED,
    { ...filterProps },
    { integrations: { Intercom: false } }
  );
};

const upgradeOpportunity = (name: string, feature: string) => {
  const params: TrackingProperties = {
    name,
    feature,
  };

  const options = { integrations: { Intercom: false } };

  TimelyAnalytics.track(UPGRADE_OPPORTUNITY, params, options);
};

const startedTrial = (accountId: number, userId: number) => {
  const params: TrackingProperties = {};

  const options = {
    integrations: {
      All: false,
      'Google Analytics': true,
      'Facebook Pixel': true,
    },
  };

  TimelyAnalytics.track(TRIAL_STARTED, params, options);
  window.dataLayer ??= [];
  window.dataLayer.push({
    event: TRIAL_STARTED_DATALAYER,
    workspaceID: accountId,
    memory_user_id: userId,
  });
};

const signedUp = (email: string, accountId: number, userId: number) => {
  TimelyAnalytics.track(
    SIGNED_UP,
    // label is the name being used by GA here
    { email, label: accountId },
    { integrations: { Intercom: false } }
  );
  window.dataLayer ??= [];
  window.dataLayer.push({
    event: SIGNED_UP_DATALAYER,
    workspaceID: accountId,
    memory_user_id: userId,
  });
};

const snoozeMemoryBanner = (count: number) => {
  TimelyAnalytics.track(SNOOZE_MEMORY_BANNER, { count }, { integrations: { Intercom: false } });
};

const saveUserProperty = (key: AllAcceptedUserPropertyKey, value: UserPropertyValue = true) => {
  if (isUserPropertyInBlacklist(key)) {
    return;
  }

  const trackingProperties = {
    property_name: key,
  };

  const possibleValue =
    value !== null && value !== undefined
      ? { property_value: Array.isArray(value) ? value.join(',') : value }
      : undefined;

  const property = USER_PROPERTIES_EVENT_NAMES[key as CustomEventNameUserPropertyKey];
  if (property) {
    TimelyAnalytics.track(
      property,
      { ...trackingProperties, ...possibleValue },
      { integrations: { Intercom: false } }
    );
  }
};

const downloadedMemory = (source: DownloadMemorySource | null, app?: AppId) => {
  const params: TrackingProperties = {};

  if (source) {
    params.source = source;
  }

  if (app) {
    params.app = app;
  }

  TimelyAnalytics.track(DOWNLOAD_MEMORY_TRACKER, params);
};

const connectedApp = (name: string) => {
  TimelyAnalytics.track(CONNECTED_APP, { name }, { integrations: { Intercom: false } });
};

const startCancelSubscription = () => {
  TimelyAnalytics.track(
    'Started Cancellation',
    { step: 'unknown' },
    {
      integrations: {
        intercom: true,
      },
    }
  );
};
const confirmCancelSubscription = () => {
  TimelyAnalytics.track(
    'Confirmed Cancellation',
    { step: 'unknown' },
    {
      integrations: {
        intercom: true,
      },
    }
  );
};

const exportedReport = (
  source: ExportReportTrackingSource,
  format: SupportedExtensions,
  options: {
    columns: Array<string>;
    totalsOnly?: boolean;
  },
  reportId?: number
) => {
  TimelyAnalytics.track(EXPORTED_REPORT, {
    type: format,
    source,
    version: 'new',
    columns: options.columns,
    totalsOnly: options.totalsOnly,
    reportId,
  });
};

/**
 * Tracking "initiating actions", like clicking a button to start inviting a new user
 * Not to be confused by "completed action", e.g "Invited user".
 */
const initiateUserAction = (action: string) => {
  TimelyAnalytics.track(INITIATED_USER_ACTION, {
    text: action,
  });
};

const addedMemoryToTimeEntry = (state: Readonly<IState>, dragged = false) => {
  const hasDraft = !!getHourDraft(state);
  const standalone = hasDraft && getUserPropertyAsBoolean(state, SHOW_HOUR_EDITOR_ATTACHMENTS);
  const { span: timespan } = getCalendarFilter(state);
  const memoryViewmode = getMemoryViewmode(state);
  const memory_experience = memoryViewmode.tic ? 'New' : 'Old';
  const mode = memoryViewmode.viewmode === 'timeline' ? 'Timeline' : 'List';

  TimelyAnalytics.track(ADDED_MEMORY_TO_ENTRY, {
    standalone,
    mode,
    timespan,
    dragged,
    memory_experience,
  });
};

export const TrackingActions = {
  initiateUserAction,
  viewedHint,
  viewedBanner,
  viewedSuggestionDropdown,
  viewedTerms,
  trainedMemory,
  userDashboardFilterChanged,
  upgradeOpportunity,
  startedTrial,
  signedUp,
  snoozeMemoryBanner,
  saveUserProperty,
  downloadedMemory,
  connectedApp,
  startCancelSubscription,
  confirmCancelSubscription,
  exportedReport,
  addedMemoryToTimeEntry,
};
